import { computed, Ref, watch } from 'vue'
import { type JobFilter, JobFilterSet, type JobFilterSetDTO } from '@ironsight/types'
import { useJobFilterSetsQuery } from '@/api/useJobFilterSetsQuery'
import { BUCKET_FILTER_PROPERTIES, BucketProperty } from './useJobFilterBuckets'
import { usePersistedJobFilterSetOrder } from './usePersistedJobFilterSetOrder'

const LIMITS = {
  MAX_SAVED_SETS: 20,
  MAX_SET_NAME_LENGTH: 64,
  MIN_NAME_LENGTH: 3,
} as const

type JobFilterSetActions = {
  loadFilterSet: (filterSet: JobFilterSet) => void
  clearFilters: () => void
  setSelectedFilterSetId: (id: string | null) => void
}

export function useJobFilterSets(
  userSid: Ref<string | null>,
  isGlobalAdmin: Ref<boolean>,
  getFilters: Ref<JobFilter[]>,
  actions: JobFilterSetActions,
) {
  const {
    data: getFilterSets,
    isFetching,
    error: queryError,
    createMutation,
    deleteMutation,
    updateMutation,
  } = useJobFilterSetsQuery()
  const { preferredOrder, reload, reconcile, moveToFront, reorderVisible } =
    usePersistedJobFilterSetOrder(userSid)

  const hasLoadedFilterSets = computed(() => getFilterSets?.value !== undefined)
  const filterSets = computed(() => getFilterSets?.value ?? [])

  const hasError = computed(
    () =>
      !!(
        queryError.value ||
        createMutation.error.value ||
        deleteMutation.error.value ||
        updateMutation.error.value
      ),
  )

  const orderedFilterSets = computed(() => {
    if (!filterSets.value.length) return []

    const ordered: JobFilterSet[] = []
    const remaining = new Set(filterSets.value)

    for (const id of preferredOrder.value) {
      const item = filterSets.value.find((set) => set.id === id)
      if (item && remaining.has(item)) {
        ordered.push(item)
        remaining.delete(item)
      }
    }

    return [...ordered, ...remaining]
  })

  const saveButtonTooltip = computed(() => {
    if (isGlobalAdmin.value) return 'Global admins cannot save filters'
    if (!hasFilters.value) return 'Save current filters'
    if (filterSets.value.length >= LIMITS.MAX_SAVED_SETS) {
      return `Maximum ${LIMITS.MAX_SAVED_SETS} saved filters`
    }
    return 'Save current filters'
  })

  const stripBucketManagedFilters = (filters: JobFilter[]): JobFilter[] => {
    return filters.filter(
      (filter) => !BUCKET_FILTER_PROPERTIES.includes(filter.property as BucketProperty),
    )
  }

  const hasFilters = computed(() => stripBucketManagedFilters(getFilters.value).length > 0)

  const filtersEqual = (a: JobFilter[], b: JobFilter[]): boolean => {
    const sanitizedA = stripBucketManagedFilters(a)
    const sanitizedB = stripBucketManagedFilters(b)

    if (sanitizedA.length !== sanitizedB.length) return false

    const sortedA = [...sanitizedA].sort((x, y) => x.id?.localeCompare(y.id || '') || 0)
    const sortedB = [...sanitizedB].sort((x, y) => x.id?.localeCompare(y.id || '') || 0)

    return JSON.stringify(sortedA) === JSON.stringify(sortedB)
  }

  const validateFilterSetName = (name: string, isUpdate = false): string | null => {
    const trimmedName = name.trim()

    if (!trimmedName) return 'Name is required'

    if (
      trimmedName.length < LIMITS.MIN_NAME_LENGTH ||
      trimmedName.length > LIMITS.MAX_SET_NAME_LENGTH
    ) {
      return `Name must be between ${LIMITS.MIN_NAME_LENGTH} and ${LIMITS.MAX_SET_NAME_LENGTH} characters`
    }

    if (!isUpdate && !hasFilters.value) return 'No filters to save'

    if (filterSets.value.some((set) => set.name.toLowerCase() === trimmedName.toLowerCase())) {
      return 'Filter set name must be unique'
    }

    if (!isUpdate && filterSets.value.length >= LIMITS.MAX_SAVED_SETS) {
      return `Current maximum ${LIMITS.MAX_SAVED_SETS} filter sets reached`
    }

    return null
  }

  const moveFilterSetIdToFront = (filterSetId: string) => {
    moveToFront(filterSetId)
  }

  const reorderVisibleFilterSets = (visibleFilterSetIds: string[]) => {
    reorderVisible(visibleFilterSetIds)
  }

  watch(
    filterSets,
    (nextFilterSets) => {
      if (!hasLoadedFilterSets.value) {
        return
      }

      const availableIds = nextFilterSets.map((set) => set.id)

      if (userSid.value) {
        reload()
      }

      reconcile(availableIds)
    },
    {
      immediate: true,
    },
  )

  const createFilterSet = async (name: string): Promise<{ success: boolean; error?: string }> => {
    const validationError = validateFilterSetName(name)
    if (validationError) return { success: false, error: validationError }

    const filters = stripBucketManagedFilters(getFilters.value).map((filter) => ({ ...filter }))

    const filterSetDTO: JobFilterSetDTO = {
      name: name.trim(),
      filters,
    }

    return new Promise((resolve) => {
      createMutation.mutate(filterSetDTO, {
        onSuccess: (response) => {
          const newSetId = response.data.id as string
          actions.setSelectedFilterSetId(newSetId)
          moveFilterSetIdToFront(newSetId)
          resolve({ success: true })
        },
        onError: () => resolve({ success: false, error: 'Failed to create filter set' }),
      })
    })
  }

  const updateFilterSet = async (
    id: string,
    name: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const validationError = validateFilterSetName(name, true)
    if (validationError) return { success: false, error: validationError }

    const currentFilterSet = filterSets.value.find((set) => set.id === id)
    if (!currentFilterSet) return { success: false, error: 'Filter set not found' }

    const filterSetDTO: JobFilterSetDTO = {
      name: name.trim(),
      filters: currentFilterSet.data.filters,
    }

    return new Promise((resolve) => {
      updateMutation.mutate(
        { id, filterSetDTO },
        {
          onSuccess: () => resolve({ success: true }),
          onError: () => resolve({ success: false, error: 'Failed to update filter set' }),
        },
      )
    })
  }

  const deleteFilterSet = async (id: string): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      deleteMutation.mutate(id, {
        onSuccess: () => resolve({ success: true }),
        onError: () => resolve({ success: false, error: 'Failed to delete filter set' }),
      })
    })
  }

  const loadFilterSet = (filterSet: JobFilterSet) => {
    actions.loadFilterSet(filterSet)
  }

  const loadFilterSetAndMoveToFront = (filterSet: JobFilterSet) => {
    loadFilterSet(filterSet)
    moveFilterSetIdToFront(filterSet.id)
  }

  const clearAllFilters = () => {
    actions.clearFilters()
  }

  const isFilterSetSelected = (filterSet: JobFilterSet, selectedFilterSetId: string | null) => {
    if (!selectedFilterSetId || selectedFilterSetId !== filterSet.id) return false
    return filtersEqual(getFilters.value, filterSet.data.filters)
  }

  return {
    filterSets,
    orderedFilterSets,
    hasFilters,
    hasError,
    isFetching,
    saveButtonTooltip,
    isCreating: computed(() => createMutation.isLoading.value),
    isUpdating: computed(() => updateMutation.isLoading.value),
    isDeleting: computed(() => deleteMutation.isLoading.value),
    createFilterSet,
    updateFilterSet,
    deleteFilterSet,
    loadFilterSet,
    loadFilterSetAndMoveToFront,
    clearAllFilters,
    moveFilterSetIdToFront,
    reorderVisibleFilterSets,
    validateFilterSetName,
    filtersEqual,
    isFilterSetSelected,
    LIMITS,
  }
}
